import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Loader2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProductCard from "@/components/ProductCard";

const HERO = "https://images.unsplash.com/photo-1685489807405-fdffb06aef2c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBqZXdlbHJ5JTIwc3RvcmUlMjBnb2xkfGVufDB8fHx8MTc4NjQwMzY0M3ww&ixlib=rb-4.1.0&q=85";

function Section({ title, subtitle, onMore, children, testid }) {
  return (
    <section className="mb-10" data-testid={testid}>
      <div className="flex items-end justify-between mb-4">
        <div>
          <h2 className="font-display text-2xl text-foreground">{title}</h2>
          {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
        {onMore && (
          <button onClick={onMore} className="text-xs text-gold flex items-center gap-1 hover:gap-2 transition-all">
            Tümü <ChevronRight size={14} />
          </button>
        )}
      </div>
      {children}
    </section>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [p, c] = await Promise.all([
          api.get("/products", { params: { sort: "newest" } }),
          api.get("/categories"),
        ]);
        setProducts(p.data);
        setCategories(c.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="animate-spin text-gold" size={28} />
      </div>
    );
  }

  const newest = products.slice(0, 6);
  const collections = products.slice(0, 8);

  return (
    <div>
      <div className="relative rounded-lg overflow-hidden border border-border mb-8 animate-fade-up">
        <img src={HERO} alt="ORSE GOLD" className="w-full h-48 sm:h-64 object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-center px-6 sm:px-10">
          <div className="text-[10px] tracking-luxe uppercase text-gold mb-2">Hoş geldiniz, {user?.company_name || user?.name}</div>
          <img src="/orse-logo.png" alt="ORSE GOLD" className="h-14 sm:h-16 w-auto object-contain drop-shadow-2xl" />
          <p className="text-white/70 text-sm mt-3">Toptan Kuyumculuk Koleksiyonları</p>
        </div>
      </div>

      <Section title="Kategoriler" testid="home-categories" onMore={() => navigate("/categories")}>
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
          {categories.map((c) => (
            <button
              key={c.id}
              data-testid={`home-category-${c.name}`}
              onClick={() => navigate(`/category/${encodeURIComponent(c.name)}`)}
              className="shrink-0 px-4 py-2 rounded-full border border-border text-sm text-muted-foreground hover:border-gold hover:text-gold transition-colors whitespace-nowrap"
            >
              {c.name}
            </button>
          ))}
        </div>
      </Section>

      <Section title="Yeni Gelenler" subtitle="Son eklenen ürünler" testid="home-new" onMore={() => navigate("/search")}>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {newest.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </Section>

      <Section title="Koleksiyonlar" subtitle="Öne çıkan ürünler" testid="home-collections">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {collections.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
        {collections.length === 0 && (
          <div className="text-center py-12 text-muted-foreground flex flex-col items-center gap-2">
            <Sparkles size={28} strokeWidth={1.5} />
            <p className="text-sm">Henüz ürün bulunmuyor.</p>
          </div>
        )}
      </Section>
    </div>
  );
}
